import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["onClick"],
  _excluded2 = ["highlighted", "aria-disabled", "help", "isIndeterminate", "isSelected", "id", "itemComponent", "toggleable", "_useButton", "_useDropdownText", "onMouseEnter", "onToggle", "enableKeyboardScrollingNav", "message", "option", "search"];
import { cloneElement, forwardRef, isValidElement, memo } from 'react';
import { Checkbox } from '../Checkbox';
import { stopPropagationHandler } from '../../utils/DomEvents';
import { TFlex } from '../TFlex';
import { LoadingSpinner } from '../LoadingSpinner';
import getComponentName from 'react-utils/getComponentName';
import { warnIfFragment } from '../../utils/warnIfFragment';
import { Image } from '../Image';
import { Tag } from '../Tag';
import { Badge } from '../Badge';
import { Button } from '../Button';
import { Text } from '../../../components/Text';
import styled from 'foundations-theming/utils';
import { View, StyledView } from '../../../components/View/View';
import { SpinnerOuter } from '../LoadingSpinner/LoadingSpinner';
import { UnstyledButton } from '../Button/PrivateButton';
import { useIsDisplayOptimized } from '../../context/DisplayOptimizedContext';
import { StyledText } from '../../../components/Text/Text';
import { CSS_SPECIFICITY } from '../../constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BLANK_LABEL = '\u00A0';
export const StyledButtonWrapper = styled.span.withConfig({
  displayName: "TypeaheadResultsItem__StyledButtonWrapper"
})(() => {
  return {
    display: 'block'
  };
});
export const StyledContentLabel = styled.span.withConfig({
  displayName: "TypeaheadResultsItem__StyledContentLabel"
})(() => {
  return {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [`& ${SpinnerOuter}`]: {
      transform: 'scale(0.75)'
    }
  };
});
export const StyledContentWrapper = styled.span.withConfig({
  displayName: "TypeaheadResultsItem__StyledContentWrapper"
})(({
  $toggleable,
  theme
}) => {
  return Object.assign({
    display: 'flex',
    alignItems: 'center'
  }, $toggleable && {
    justifyContent: 'center',
    paddingInlineEnd: theme.space['400'],
    fontSize: theme.typography['body-100'].fontSize
  }, {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    [`& ${StyledView}:has(.country-flag)`]: {
      marginInlineEnd: theme.space['200'],
      marginInlineStart: theme.space['100'],
      '& .country-flag': {
        verticalAlign: 'middle'
      }
    }
  });
});
export const StyledItem = styled.li.withConfig({
  displayName: "TypeaheadResultsItem__StyledItem"
})(({
  $disabled = false,
  $highlighted,
  $isCustomComponent,
  $message,
  $help,
  $toggleable,
  theme
}) => {
  const isHighlighted = $highlighted && !$disabled;
  return Object.assign({}, $isCustomComponent && {
    display: 'block'
  }, $message && {}, !$message && {
    [`& ${UnstyledButton}`]: {
      maxInlineSize: '100%' // mimic truncate-text classname in UITypeaheadResultsItem
    }
  }, isHighlighted && {
    backgroundColor: theme.color['fill-field-hover']
  }, {
    // This forces override of background color to match .private-button--unstyled and
    // to ensure that these styles override the button styling for dropdownStyleOverrides
    // which targets all nested buttons and sets background color causing issues with help text
    // @see https://git.hubteam.com/HubSpot/foundations-components/blob/master/foundations-components/static-1.7338/js/transitional/components/Dropdown/Dropdown.tsx#L227-L241
    [`${CSS_SPECIFICITY.FORCE_OVERRIDE} ${UnstyledButton}`]: {
      backgroundColor: 'unset'
    }
  }, $disabled && {
    color: theme.color['text-primary-disabled'],
    backgroundColor: theme.color['fill-surface-default'],
    borderColor: theme.color['fill-primary-disabled'],
    [`& ${UnstyledButton}`]: {
      color: theme.color['text-primary-disabled'],
      cursor: 'not-allowed'
    }
  }, $help && $toggleable && {
    [`& ${StyledText}`]: {
      marginInlineStart: '34px' // no spacing token matching the width of a checkbox :(
    }
  });
});
const getLoadingIndicator = computedButtonText => {
  return /*#__PURE__*/_jsxs(TFlex, {
    align: "center",
    children: [computedButtonText, /*#__PURE__*/_jsx(LoadingSpinner, {
      marginBlock: "0",
      marginInline: "0",
      marginInlineStart: "100",
      size: "xs",
      use: "tertiary"
    })]
  });
};
const getLabel = ({
  option: {
    buttonText,
    dropdownText,
    loading,
    text,
    value
  },
  _useDropdownText
}) => {
  const fallbackText = text || (value == null ? '' : value.toString());
  if (_useDropdownText) {
    return dropdownText == null ? fallbackText : dropdownText;
  }
  const computedButtonText = buttonText == null ? fallbackText : buttonText;
  if (loading) {
    return getLoadingIndicator(`${computedButtonText}`);
  } else {
    return computedButtonText;
  }
};
const getTitle = ({
  option: {
    text,
    title,
    value
  }
}) => {
  if (title === '') {
    return undefined;
  }
  return title || text || (value == null ? '' : value.toString());
};
const renderHelp = ({
  option: {
    disabled,
    help
  }
}) => {
  if (help) {
    return /*#__PURE__*/_jsx(Text, {
      as: "small",
      marginBlockStart: "-50",
      paddingBlockStart: "0",
      paddingInline: "400",
      paddingBlockEnd: "200",
      whiteSpace: "normal",
      wordBreak: "break-word",
      display: "block",
      typography: "detail-200",
      color: disabled ? 'text-primary-disabled' : 'text-core-subtle',
      children: help
    });
  }
  return null;
};
const renderLabel = ({
  search,
  computedLabel
}) => {
  if (!computedLabel) {
    return BLANK_LABEL;
  }
  if (!search || typeof computedLabel !== 'string') {
    return computedLabel;
  }
  const searchResult = search.exec(computedLabel);
  if (!searchResult) {
    return computedLabel;
  }
  const match = searchResult[0];
  const head = computedLabel.slice(0, searchResult.index);
  const tail = computedLabel.slice(searchResult.index + match.length);
  return /*#__PURE__*/_jsxs("span", {
    children: [head, /*#__PURE__*/_jsx("span", {
      children: match
    }), tail]
  });
};
const renderItemImage = ({
  option: {
    avatar,
    icon,
    iconColor,
    imageUrl
  },
  isDisplayOptimized
}) => {
  if (avatar) {
    const isValidAvatarElement = /*#__PURE__*/isValidElement(avatar);
    const size = isValidAvatarElement ? avatar.props.size : undefined;
    // @ts-expect-error TODO: fix this soon
    const isAvatar = ['UIAvatar', 'Avatar'].includes(getComponentName(avatar));
    warnIfFragment(avatar, 'TypeaheadResultsItem', 'avatar');
    const avatarEl = isValidAvatarElement ? /*#__PURE__*/cloneElement(avatar, Object.assign({}, isAvatar ? {
      size: 'responsive'
    } : {
      size,
      shape: 'circle'
    })) : null;
    return /*#__PURE__*/_jsx(View, {
      display: "inline-block"
      // Use smaller dimensions (22px vs 24px) for avatars to maintain consistent
      // 40px height when used within Select components
      ,
      maxInlineSize: isAvatar ? '22px' : '24px',
      maxBlockSize: isAvatar ? '22px' : '24px',
      marginInlineEnd: isDisplayOptimized ? '100' : '300',
      children: avatarEl
    });
  }
  if (icon) {
    const Icon = icon;
    return /*#__PURE__*/_jsx(View, {
      display: "inline-block",
      marginInlineEnd: "300",
      position: "relative",
      insetBlockStart: "50",
      children: /*#__PURE__*/_jsx(Icon, {
        size: "200",
        color: iconColor
      })
    });
  }
  if (imageUrl) {
    return /*#__PURE__*/_jsx(Image, {
      src: imageUrl,
      shape: "circle",
      responsive: false,
      blockSize: "24px",
      marginInlineEnd: "400"
    });
  }
  return null;
};
const renderTag = ({
  option: {
    tag
  }
}) => {
  if (!tag) {
    return null;
  }
  return /*#__PURE__*/_jsx(Tag, {
    inline: true,
    use: tag.use,
    children: tag.text
  });
};
const renderBadge = ({
  option: {
    badge
  }
}) => {
  if (!badge) {
    return null;
  }
  if ( /*#__PURE__*/isValidElement(badge)) {
    warnIfFragment(badge, 'TypeaheadResultsItem', 'badge');
    return /*#__PURE__*/cloneElement(badge, {
      marginInlineStart: '200',
      marginBlockStart: '-50'
    });
  }
  if (typeof badge === 'object' && 'use' in badge) {
    return /*#__PURE__*/_jsx(Badge, {
      marginInlineStart: "200",
      marginBlockStart: "-50",
      use: badge.use,
      children: badge.text
    });
  }
  return null;
};
const MemoizedContent = /*#__PURE__*/memo(({
  renderedItemImage,
  renderedLabel,
  renderedTag,
  renderedBadge,
  toggleable
}) => /*#__PURE__*/_jsxs(StyledContentWrapper, {
  $toggleable: toggleable,
  children: [renderedItemImage, ' ', /*#__PURE__*/_jsx(StyledContentLabel, {
    "data-option-text": true,
    children: renderedLabel
  }), ' ', renderedTag, " ", renderedBadge]
}));
const MemoizedButton = /*#__PURE__*/memo(_ref => {
  let {
      option,
      rest: {
        onClick
      },
      computedTitle,
      renderedContent,
      renderedHelp
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref.rest, _excluded);
  return /*#__PURE__*/_jsxs(StyledButtonWrapper, {
    onClick: onClick,
    children: [/*#__PURE__*/_jsx(Button, Object.assign({}, rest, {
      paddingBlock: "200",
      paddingInline: "500",
      tabIndex: -1,
      title: computedTitle,
      use: "unstyled",
      responsive: false,
      disabled: option.disabled,
      children: renderedContent
    })), renderedHelp]
  });
});
const MemoizedCheckbox = /*#__PURE__*/memo(({
  isIndeterminate,
  isSelected,
  onToggle,
  computedTitle,
  option,
  renderedContent,
  renderedHelp
}) => /*#__PURE__*/_jsxs("label", {
  onMouseDown: stopPropagationHandler,
  children: [/*#__PURE__*/_jsx(Checkbox, {
    paddingBlock: "200",
    paddingInline: "500",
    checked: isSelected,
    disabled: option.disabled || isSelected && option.clearableValue === false,
    id: `checkbox-${option.value}`,
    indeterminate: isIndeterminate,
    onChange: onToggle,
    title: computedTitle,
    children: renderedContent
  }), renderedHelp]
}));

// Invoking useIsDisplayOptimized directly on TypeaheadResultsItem was causing a lot of unecessary re-renders
const MemoizedItemImage = /*#__PURE__*/memo(({
  option
}) => {
  const isDisplayOptimized = useIsDisplayOptimized();
  return renderItemImage({
    option,
    isDisplayOptimized
  });
});
export const TypeaheadResultsItem = /*#__PURE__*/memo( /*#__PURE__*/forwardRef((_ref2, ref) => {
  let {
      highlighted,
      'aria-disabled': ariaDisabled = false,
      help,
      isIndeterminate,
      isSelected,
      id,
      itemComponent: Item = 'li',
      toggleable = false,
      _useButton = true,
      _useDropdownText = true,
      onMouseEnter,
      onToggle,
      enableKeyboardScrollingNav,
      message,
      option,
      search
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  // @ts-expect-error Escape hatch for dividers??
  if (option.rawNode) return option.rawNode;
  const computedAriaSelected = !!highlighted;
  const computedTitle = getTitle({
    option
  });
  const computedLabel = getLabel({
    option,
    _useDropdownText
  });
  const renderedItemImage = /*#__PURE__*/_jsx(MemoizedItemImage, {
    option: option
  });
  const renderedLabel = renderLabel({
    search,
    computedLabel
  });
  const renderedTag = renderTag({
    option
  });
  const renderedBadge = renderBadge({
    option
  });
  let renderedContent = /*#__PURE__*/_jsx(MemoizedContent, {
    renderedItemImage: renderedItemImage,
    renderedLabel: renderedLabel,
    renderedTag: renderedTag,
    renderedBadge: renderedBadge,
    toggleable: toggleable
  });
  if (_useButton) {
    const renderedHelp = renderHelp({
      option
    });
    renderedContent = /*#__PURE__*/_jsx(MemoizedButton, {
      option: option,
      rest: rest,
      computedTitle: computedTitle,
      renderedContent: renderedContent,
      renderedHelp: renderedHelp
    });
  }
  if (toggleable) {
    const renderedHelp = renderHelp({
      option
    });
    renderedContent = /*#__PURE__*/_jsx(MemoizedCheckbox, {
      computedTitle: computedTitle,
      isIndeterminate: isIndeterminate,
      isSelected: isSelected,
      onToggle: onToggle,
      option: option,
      renderedContent: renderedContent,
      renderedHelp: renderedHelp
    });
  }
  if (typeof Item === 'function') {
    if (enableKeyboardScrollingNav) {
      return /*#__PURE__*/_jsx(StyledItem, {
        $disabled: option.disabled,
        $highlighted: highlighted,
        $help: help,
        $toggleable: toggleable,
        $message: message,
        as: 'li',
        ref: ref,
        children: /*#__PURE__*/_jsx(Item, Object.assign({}, rest, {
          "aria-selected": computedAriaSelected,
          "aria-disabled": option.disabled || ariaDisabled || undefined,
          "data-option-value": option.value != null ? String(option.value) : undefined,
          id: id,
          onMouseEnter: onMouseEnter,
          option: option,
          role: "option",
          children: renderedContent
        }))
      });
    }
    return /*#__PURE__*/_jsx(StyledItem, {
      $disabled: option.disabled,
      $help: help,
      $highlighted: highlighted,
      $toggleable: toggleable,
      $isCustomComponent: true,
      $message: message,
      as: 'li',
      ref: ref,
      children: /*#__PURE__*/_jsx(Item, Object.assign({}, rest, {
        "aria-selected": computedAriaSelected,
        "aria-disabled": option.disabled || ariaDisabled || undefined,
        "data-option-value": option.value != null ? String(option.value) : undefined,
        id: id,
        onMouseEnter: onMouseEnter,
        option: option,
        role: "option",
        children: renderedContent
      }))
    });
  }

  /**
   * Ensure the onMouseDown callback is triggered when the empty space in the TypeaheadResultsItem is clicked.
   * @see https://git.hubteam.com/HubSpot/foundations-components/issues/5837
   */
  const maybeTriggerCallback = (evt, callback) => {
    const {
      target
    } = evt;
    if (target instanceof HTMLElement && !target.hasAttribute('data-option-text')) {
      callback === null || callback === void 0 || callback(evt);
    }
  };
  return /*#__PURE__*/_jsx(StyledItem, {
    $disabled: option.disabled,
    $help: help,
    $highlighted: highlighted,
    $toggleable: toggleable,
    $message: message,
    as: Item,
    option: option,
    "aria-selected": computedAriaSelected,
    "aria-disabled": option.disabled || ariaDisabled || undefined,
    "data-option-value": option.value,
    id: id,
    onMouseEnter: onMouseEnter,
    onMouseDown: evt => maybeTriggerCallback(evt, rest.onMouseDown),
    role: "option",
    ref: ref,
    "data-typeahead-item": true,
    children: renderedContent
  });
}));
TypeaheadResultsItem.displayName = 'TypeaheadResultsItem';